import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const InteractivePoll = () => {
    const { user } = useAuth();
    const [poll, setPoll] = useState(null);
    const [selectedOption, setSelectedOption] = useState(null);
    const [hasVoted, setHasVoted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [message, setMessage] = useState('');

    const textGradientStyle = {
        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        color: 'transparent'
    };

    const buttonGradientStyle = {
        background: 'linear-gradient(to right, #033f83, #079acf, #65bade)'
    };

    useEffect(() => {
        fetchActivePoll();
        const interval = setInterval(fetchActivePoll, 5000); // Poll every 5 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchActivePoll = async () => {
        try {
            const res = await api.get('/polls/active');
            if (res.data) {
                // If it's a new poll, reset state
                if (!poll || poll.id !== res.data.id) {
                    setPoll(res.data);
                    setHasVoted(res.data.hasVoted);
                    const savedOption = localStorage.getItem(`voted_poll_${res.data.id}`);
                    setSelectedOption(savedOption ? parseInt(savedOption) : null);
                    setMessage(res.data.hasVoted ? 'Ya ha votado en esta pregunta.' : '');
                } else if (res.data.hasVoted && !hasVoted) {
                    setHasVoted(true);
                    setMessage('¡Voto registrado con éxito!');
                }
            } else {
                setPoll(null);
            }
        } catch (error) {
            console.error("Error fetching poll", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleVote = async () => {
        if (!selectedOption) return;
        try {
            await api.post(`/polls/${poll.id}/vote`, { optionId: selectedOption });
            setHasVoted(true);
            localStorage.setItem(`voted_poll_${poll.id}`, selectedOption);
            setMessage('¡Voto registrado con éxito!');
        } catch (error) {
            setMessage(error.response?.data?.message || 'Error al votar');
        }
    };

    if (isLoading) return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
        </div>
    );

    if (!poll) return (
        <div className="text-center py-20 animate-fadeIn bg-white rounded-[40px] border border-slate-100 p-10 shadow-xl max-w-2xl mx-auto">
            <div className="text-6xl mb-6">⏳</div>
            <h2 className="text-2xl font-black uppercase tracking-tighter" style={{ ...textGradientStyle, fontFamily: 'Drina, cursive' }}>Esperando Pregunta</h2>
            <p className="mt-4 text-sm font-bold uppercase tracking-widest" style={textGradientStyle}>Mantente conectado, el moderador lanzará una pregunta pronto.</p>
        </div>
    );

    return (
        <div className="animate-fadeIn space-y-8 max-w-3xl mx-auto pb-20">
            <div className="bg-white rounded-[40px] border border-slate-100 p-8 sm:p-12 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 p-4">
                    <span className="px-4 py-1.5 rounded-full text-white text-[10px] font-black uppercase tracking-widest animate-pulse" style={buttonGradientStyle}>En Vivo</span>
                </div>

                <h2 className="text-2xl sm:text-3xl font-black leading-tight mb-10" style={{ ...textGradientStyle, fontFamily: 'Poppins-Medium, sans-serif' }}>
                    {poll.question}
                </h2>

                <div className="space-y-4">
                    {poll.options.map((opt) => (
                        <button
                            key={opt.id}
                            onClick={() => !hasVoted && setSelectedOption(opt.id)}
                            className={`w-full p-6 h-20 rounded-3xl text-left transition-all duration-300 transform active:scale-[0.98] border shadow-sm flex items-center justify-between group ${selectedOption === opt.id
                                ? 'border-transparent text-white scale-[1.02]'
                                : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-white hover:shadow-md'
                                } ${hasVoted ? 'cursor-default' : ''}`}
                            style={selectedOption === opt.id ? buttonGradientStyle : {}}
                        >
                            <span className="font-bold text-sm sm:text-base">{opt.option_text}</span>
                            {selectedOption === opt.id && <span className="text-xl">✓</span>}
                        </button>
                    ))}
                </div>

                {message && (
                    <div className={`mt-8 p-4 rounded-2xl text-center font-bold text-xs uppercase tracking-widest ${message.includes('éxito') ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                        {message}
                    </div>
                )}

                {!hasVoted && (
                    <button
                        onClick={handleVote}
                        disabled={!selectedOption}
                        className={`mt-10 w-full py-6 rounded-3xl font-black uppercase tracking-[0.3em] text-sm transition-all duration-500 shadow-xl ${selectedOption
                            ? 'text-white hover:scale-[1.02] active:scale-95'
                            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            }`}
                        style={selectedOption ? buttonGradientStyle : {}}
                    >
                        Enviar Respuesta
                    </button>
                )}

                {hasVoted && (
                    <div className="mt-10 text-center">
                        <p className="text-[10px] font-bold uppercase tracking-widest" style={textGradientStyle}>Respuesta enviada. Espera a que el moderador cierre la votación.</p>
                    </div>
                )}
            </div>

            <div className="text-center px-4">
                <p className="text-[10px] font-bold uppercase tracking-widest leading-relaxed" style={textGradientStyle}>
                    Su participación es fundamental para el análisis clínico de este caso.
                </p>
            </div>
        </div>
    );
};

export default InteractivePoll;
